// src/components/dashboard/StorageCard.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchBucketItemCount, fetchStorageData, formatFileSize } from '../../services/api.service';
import { getUsedPercentage } from '../../utils/storage.utils';

const StorageCard = ({ type, maxSize }) => {
  const [count, setCount] = useState(0);
  const [size, setSize] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const labelMap = {
    files: 'Files',
    passwords: 'Passwords',
    banking: 'Banking',
    blockchain: 'Blockchain',
  };

  // Define the routes for each card type
  const routeMap = {
    files: '/my-files',
    passwords: '/my-passwords',
    banking: '/my-banking',
    blockchain: '/my-blockchain',
  };

  // Icon map for each storage type
  const iconMap = {
    files: 'fas fa-folder',
    passwords: 'fas fa-key',
    banking: 'fas fa-credit-card',
    blockchain: 'fas fa-link',
  };

  useEffect(() => {
    const load = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const apiBucket = type === 'banking' ? 'bankcards' : type;

        const [countRes, storageRes] = await Promise.all([
          fetchBucketItemCount(apiBucket, token),
          fetchStorageData(token),
        ]);

        const bucket = storageRes.bucketSizes.find(b => b.bucket === apiBucket) || { size: 0 };

        setCount(countRes.data.totalItems || 0);
        setSize(bucket.size);
      } catch (e) {
        console.error('StorageCard load error:', e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [type]);

  const handleCardClick = () => {
    const route = routeMap[type];
    if (route) {
      navigate(route);
    }
  };

  const usedPercentage = getUsedPercentage(size, maxSize);

  return (
    <div 
      className="card storage-card clickable-card" 
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
    >
      <div className="card-body">
        {/* Header with icon, title, and count */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center">
            <i className={`${iconMap[type]} me-2 text-success`} style={{ fontSize: '1.1rem' }}></i>
            <h6 className="text-uppercase font-weight-bold mb-0">
              {labelMap[type] || type}
            </h6>
          </div>
          {loading ? (
            <div className="spinner-border spinner-border-sm text-success" role="status" />
          ) : (
            <span className="badge badge-desktop">
              {count} items
            </span>
          )}
        </div>

        {/* Mobile badge - shown above progress bar on mobile only */}
        {!loading && (
          <div className="mb-2 badge-mobile">
            <span className="badge">
              {count} items
            </span>
          </div>
        )}

        {/* Progress bar */}
        <div className="progress">
          <div
            className="progress-bar"
            role="progressbar"
            style={{ width: `${usedPercentage}%` }}
            aria-valuenow={usedPercentage}
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>

        {/* Storage info */}
        {!loading && (
          <div className="d-flex justify-content-between align-items-center">
            <div className="small">
              <strong>{formatFileSize(size)}</strong> used of {formatFileSize(maxSize)}
              <div className="text-success font-weight-bold">{usedPercentage}% used</div>
            </div>
            <i className="fas fa-arrow-right"></i>
          </div>
        )}
      </div>
    </div>
  );
};

export default StorageCard;